import React, { useMemo } from "react";

import { CameraFront, ContentCopy, Edit } from "@mui/icons-material";
import PersonIcon from "@mui/icons-material/Person";
import { Avatar, Box, Stack } from "@mui/material";
import { ClockIcon } from "@mui/x-date-pickers";
import { makeStyles } from "tss-react/mui";

import { IconButton, OutlinedButton } from "@/components/Button";
import { ShadowCard } from "@/components/Card/ShadowCard";
import { Tag } from "@/components/Chip/Tag";
import { Spacer } from "@/components/Spacer/Spacer";
import {
  BodyBold,
  HeadlineBold,
  Label,
  Overline1Bold,
  Title1,
  Title3,
} from "@/components/Typography";
import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";

import useAppointmentCurrent from "../utils/useAppointmentCurrent";

const useStyles = makeStyles()(() => ({
  avatarSize: {
    width: 80,
    height: 80,
  },
  appointmentTypeIconSize: {
    width: 15,
  },
  tagText: {
    textTransform: "uppercase",
    fontWeight: "bold",
    paddingTop: 14,
    paddingBottom: 14,
  },
}));

interface AppointmentProps {
  useEdit?: boolean;
  onEdit?: () => void;
  onClickMenu?: (type: string, id: string) => void;
  onJoin?: () => void;
  id: string;
}

const appointmentPanelFragment = graphql(`
  fragment AppointmentPanel on Appointment {
    id
    appointmentType
    title
    details
    status
    startDate
    startTime
    endDate
    endTime
    meetingDetails {
      roomUrl
    }
    client {
      id
      email
      profile {
        id
        fullName
        mobileNumber
        avatarUrl(variant: THUMB)
      }
    }
  }
`);

const AppointmentCard: React.FC<AppointmentProps> = ({
  id,
  onEdit,
  onJoin,
}) => {
  const snackbar = useGlobalSnackbar();
  const { classes } = useStyles();

  const [appointment] = useFragment(
    appointmentPanelFragment,
    `Appointment:${id}`
  );

  const isCurrent = useAppointmentCurrent(appointment);

  const startTime = format(
    { timeofday: appointment?.startTime ?? new Date() },
    "hh:mm a"
  );
  const endTime = format(
    { timeofday: appointment?.endTime ?? new Date() },
    "hh:mm a"
  );
  const date = format(
    { date: appointment?.startDate ?? new Date() },
    "iiii, dd MMM, yyyy"
  );

  const cardTitleBgColor = useMemo(() => {
    return isCurrent
      ? colors.theme.tertiary.light
      : colors.theme.neutral.lighter;
  }, [isCurrent]);

  const cardTitle = useMemo(() => {
    return isCurrent ? "Current Appointment" : "Next Appointment";
  }, [isCurrent]);

  const profileHeaderBgColor = useMemo(() => {
    return isCurrent
      ? colors.theme.tertiary.lighter
      : colors.theme.neutral.lightest;
  }, [isCurrent]);

  const handleCopyMeetingLink = async () => {
    if (appointment?.meetingDetails?.roomUrl) {
      await navigator.clipboard.writeText(appointment.meetingDetails.roomUrl);
      snackbar.open("Meeting link copied.");
    }
  };

  return (
    <>
      <ShadowCard>
        <Box px="16px" py="8px" bgcolor={cardTitleBgColor}>
          <HeadlineBold text={cardTitle} />
        </Box>
        <Box bgcolor="white" borderTop="none">
          <Box
            display="flex"
            px="16px"
            py="24px"
            bgcolor={profileHeaderBgColor}
            alignItems="center"
            justifyContent={"center"}
          >
            <Avatar
              className={classes.avatarSize}
              src={appointment?.client.profile?.avatarUrl ?? undefined}
              alt="avatar"
            />
            <Spacer w={8} />
            <Box m={1} textOverflow="ellipsis">
              <Tag
                label={
                  appointment?.appointmentType === "online"
                    ? "online"
                    : "inperson"
                }
                icon={
                  appointment?.appointmentType === "online" ? (
                    <CameraFront />
                  ) : (
                    <PersonIcon />
                  )
                }
                className={classes.tagText}
              />
              <BodyBold text={appointment?.client.profile?.fullName} />
              <Label text={appointment?.client.email} />
              <Label text={appointment?.client.profile?.mobileNumber} />
            </Box>
          </Box>
          <Box padding="16px">
            <Box display="flex">
              <ClockIcon htmlColor={colors.mapped.surface.default} />
              <Spacer w={8} />
              <Box>
                <HeadlineBold text={date} />
                <HeadlineBold text={`${startTime} - ${endTime}`} />
              </Box>
            </Box>
            <Box height="16px" />
            {appointment?.appointmentType === "online" ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <>
                  <OutlinedButton text="Join Appointment" onClick={onJoin} />
                  <Stack spacing={1} direction="row">
                    <IconButton
                      size="smaller"
                      icon={
                        <ContentCopy
                          htmlColor={colors.mapped.surface.default}
                        />
                      }
                      onClick={handleCopyMeetingLink}
                    />
                    <IconButton
                      size="smaller"
                      icon={<Edit htmlColor={colors.mapped.surface.default} />}
                      onClick={() => onEdit?.()}
                    />
                  </Stack>
                </>
              </Box>
            ) : (
              <Box margin="auto">
                <OutlinedButton
                  text="Edit appointment"
                  startIcon={<Edit htmlColor={colors.mapped.surface.default} />}
                  onClick={() => onEdit?.()}
                />
              </Box>
            )}
          </Box>
        </Box>
      </ShadowCard>
      <Spacer h={16} />
    </>
  );
};

export default AppointmentCard;
